(() => {
  const initialized = new WeakSet();
  const tagCountRequests = new Map();

  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const formatCartCount = (count) => {
    if (!count) return '';
    return count + (count === 1 ? ' item waiting in your cart' : ' items waiting in your cart');
  };

  const initShopProtect = () => {
    const signal = document.querySelector('[data-sb-shop-protect]');
    if (!signal || signal.dataset.sbShopProtect !== 'true') return;

    document.documentElement.classList.add('sb-shop-protect');
    document.querySelectorAll('img').forEach((image) => image.setAttribute('draggable', 'false'));

    document.addEventListener('contextmenu', (event) => {
      if (event.target.closest && event.target.closest('img')) event.preventDefault();
    });
    document.addEventListener('dragstart', (event) => {
      if (event.target.closest && event.target.closest('img')) event.preventDefault();
    });
  };

  const initProductParity = (scope = document) => {
    const bars = [];
    if (scope.matches && scope.matches('[data-sb-sticky-atc]')) bars.push(scope);
    if (scope.querySelectorAll) bars.push(...scope.querySelectorAll('[data-sb-sticky-atc]'));

    bars.forEach((bar) => {
      if (initialized.has(bar)) return;
      initialized.add(bar);

      const mainForm = document.getElementById(bar.dataset.mainFormId);
      const productRoot = bar.closest('product-info') || bar.parentElement;
      const stickyVariant = bar.querySelector('[data-sb-sticky-variant]') || bar.querySelector('[name="id"]');
      const submit = bar.querySelector('[data-sb-sticky-submit]');
      const submitText = bar.querySelector('[data-sb-sticky-submit-text]');
      const price = bar.querySelector('[data-sb-sticky-price]');
      const image = bar.querySelector('[data-sb-sticky-image]');
      const notify = productRoot ? productRoot.querySelector('[data-sb-back-in-stock]') : null;
      const notifyMessage = notify ? notify.querySelector('[data-sb-back-in-stock-message]') : null;

      const getSelectedData = () => {
        if (!stickyVariant) return null;
        if (stickyVariant.tagName === 'SELECT') {
          return stickyVariant.options[stickyVariant.selectedIndex] || null;
        }
        return stickyVariant;
      };

      const updateVariant = (variantId) => {
        if (stickyVariant && variantId && stickyVariant.tagName === 'SELECT') {
          const match = Array.from(stickyVariant.options).find((option) => option.value === String(variantId));
          if (match) stickyVariant.value = match.value;
        } else if (stickyVariant && variantId) {
          stickyVariant.value = variantId;
        }

        const selected = getSelectedData();
        if (!selected) return;

        const available = selected.dataset.available === 'true';
        const selectedId = selected.value;
        const selectedTitle = selected.dataset.title || '';

        if (price && selected.dataset.price) price.textContent = selected.dataset.price;
        if (image && selected.dataset.image) image.src = selected.dataset.image;

        if (submit) {
          submit.disabled = !available;
          submit.toggleAttribute('aria-disabled', !available);
        }
        if (submitText) {
          submitText.textContent = available
            ? (window.variantStrings && window.variantStrings.addToCart) || 'Add to cart'
            : (window.variantStrings && window.variantStrings.soldOut) || 'Sold out';
        }

        if (notify) {
          notify.hidden = available;
          if (notifyMessage) {
            notifyMessage.value =
              'Please notify me when ' +
              notify.dataset.productTitle +
              ' becomes available - ' +
              notify.dataset.productUrl +
              '?variant=' +
              selectedId;
            notifyMessage.dataset.variantTitle = selectedTitle;
          }
        }
      };

      const syncFromMain = () => {
        const mainVariant = mainForm ? mainForm.querySelector('[name="id"]') : null;
        updateVariant(mainVariant ? mainVariant.value : stickyVariant && stickyVariant.value);
      };

      if (stickyVariant && stickyVariant.tagName === 'SELECT') {
        stickyVariant.addEventListener('change', () => updateVariant(stickyVariant.value));
      }

      if (mainForm) {
        mainForm.addEventListener('change', () => window.setTimeout(syncFromMain, 0));
        mainForm.addEventListener('input', () => window.setTimeout(syncFromMain, 0));
      }
      if (productRoot) {
        productRoot.addEventListener('change', () => window.setTimeout(syncFromMain, 0));
      }
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
        window.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
          const variantId = event && event.data && event.data.variant && event.data.variant.id;
          window.setTimeout(() => updateVariant(variantId || (mainForm && mainForm.querySelector('[name="id"]')?.value)), 0);
        });
      }

      let ticking = false;
      const updateVisibility = () => {
        ticking = false;
        if (!mainForm) return;
        const passedMainForm = mainForm.getBoundingClientRect().bottom < 0;
        bar.classList.toggle('is-visible', passedMainForm);
        bar.setAttribute('aria-hidden', passedMainForm ? 'false' : 'true');
      };
      const requestVisibilityUpdate = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(updateVisibility);
      };

      window.addEventListener('scroll', requestVisibilityUpdate, { passive: true });
      window.addEventListener('resize', requestVisibilityUpdate);
      syncFromMain();
      updateVisibility();
    });
  };

  const fetchTagCountPage = async (handle, page) => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = new URL(root + 'collections/' + encodeURIComponent(handle), window.location.origin);
    url.searchParams.set('view', 'sb-tag-counts');
    url.searchParams.set('page', String(page));

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });
    if (!response.ok) throw new Error('Unable to load collection tag counts');

    const data = await response.json();
    if (!data || typeof data.counts !== 'object') {
      throw new Error('Invalid collection tag count response');
    }
    return data;
  };

  const loadTagCounts = (handle) => {
    if (tagCountRequests.has(handle)) return tagCountRequests.get(handle);

    const request = (async () => {
      const firstPage = await fetchTagCountPage(handle, 1);
      const counts = { ...(firstPage.counts || {}) };
      const pages = Math.max(1, Number(firstPage.pages) || 1);

      for (let page = 2; page <= pages; page += 1) {
        const pageData = await fetchTagCountPage(handle, page);
        Object.entries(pageData.counts || {}).forEach(([tag, count]) => {
          counts[tag] = Number(counts[tag] || 0) + Number(count || 0);
        });
      }

      return counts;
    })().catch((error) => {
      tagCountRequests.delete(handle);
      throw error;
    });

    tagCountRequests.set(handle, request);
    return request;
  };

  const applyTagCounts = (handle, counts) => {
    document.querySelectorAll('[data-sb-tag-row]').forEach((row) => {
      if (row.dataset.sbCollectionHandle !== handle) return;

      const key = (row.dataset.sbTagValue || '').trim().toLowerCase();
      const count = Number(counts[key] || 0);
      const label = row.dataset.sbTagLabel || row.dataset.sbTagValue || '';
      const link = row.querySelector('a');

      if (link) link.textContent = label + ' (' + count + ')';
      row.hidden = count < 1 && row.dataset.sbTagActive !== 'true';
    });
  };

  const initTagFilterCounts = (scope = document) => {
    const sources = [];
    if (scope.matches && scope.matches('[data-sb-tag-count-source]')) sources.push(scope);
    if (scope.querySelectorAll) sources.push(...scope.querySelectorAll('[data-sb-tag-count-source]'));

    sources.forEach((source) => {
      if (initialized.has(source)) return;
      initialized.add(source);

      const handle = source.dataset.sbCollectionHandle;
      const collectionSize = Number(source.dataset.sbCollectionSize || 0);
      if (!handle || collectionSize <= 50) return;

      loadTagCounts(handle)
        .then((counts) => applyTagCounts(handle, counts))
        .catch(() => {});
    });
  };

  const initCartReminder = () => {
    const reminder = document.querySelector('[data-sb-cart-reminder]');
    if (!reminder || initialized.has(reminder)) return;
    initialized.add(reminder);

    const image = reminder.querySelector('[data-sb-cart-reminder-image]');
    const imageWrap = reminder.querySelector('[data-sb-cart-reminder-image-wrap]');
    const countText = reminder.querySelector('[data-sb-cart-reminder-count]');
    const close = reminder.querySelector('[data-sb-cart-reminder-close]');
    let cartCount = Number(reminder.dataset.cartCount || 0);
    let hideTimer;
    let intervalTimer;

    const hide = () => {
      reminder.hidden = true;
      window.clearTimeout(hideTimer);
    };

    const show = () => {
      if (cartCount < 1 || window.matchMedia('(max-width: 749px)').matches) return;
      reminder.hidden = false;
      window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(hide, 7000);
    };

    const ensureSchedule = () => {
      if (intervalTimer || cartCount < 1) return;
      intervalTimer = window.setInterval(show, 10000);
      window.setTimeout(show, 10000);
    };

    const updateFromCart = (cart) => {
      cartCount = Number(cart && cart.item_count ? cart.item_count : 0);
      reminder.dataset.cartCount = String(cartCount);
      if (countText) countText.textContent = formatCartCount(cartCount);

      const firstItem = cart && cart.items && cart.items[0];
      if (image && imageWrap && firstItem && firstItem.image) {
        image.src = firstItem.image;
        image.alt = firstItem.product_title || '';
        imageWrap.hidden = false;
      }

      if (cartCount < 1) {
        hide();
        window.clearInterval(intervalTimer);
        intervalTimer = null;
      } else {
        ensureSchedule();
      }
    };

    const refresh = () => {
      fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : reminder.dataset.cartUrl) + '.js', {
        headers: { Accept: 'application/json' },
      })
        .then((response) => (response.ok ? response.json() : Promise.reject(new Error('Cart request failed'))))
        .then(updateFromCart)
        .catch(() => {});
    };

    if (close) close.addEventListener('click', hide);
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, refresh);
    }
    document.addEventListener('cart:updated', refresh);
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (form instanceof HTMLFormElement && String(form.action || '').includes('/cart/add')) {
          window.setTimeout(refresh, 1200);
        }
      },
      true
    );

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      let drawerRefreshTimer;
      new MutationObserver(() => {
        window.clearTimeout(drawerRefreshTimer);
        drawerRefreshTimer = window.setTimeout(refresh, 120);
      }).observe(cartDrawer, { childList: true, subtree: true, characterData: true });
    }

    ensureSchedule();
  };

  const setupCarousel = (root, config) => {
    if (!root || initialized.has(root)) return;
    const track = root.querySelector(config.track);
    const slides = track ? Array.from(track.querySelectorAll(config.slide)) : [];
    const previous = root.querySelector(config.previous);
    const next = root.querySelector(config.next);
    const dots = Array.from(root.querySelectorAll(config.dot));
    if (!track || slides.length < 1) return;

    initialized.add(root);
    let current = 0;
    let scrollTimer;
    let autoplayTimer;

    const nearestIndex = () => {
      const left = track.scrollLeft;
      let best = 0;
      let bestDistance = Infinity;
      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - track.offsetLeft - left);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });
      return best;
    };

    const update = () => {
      current = nearestIndex();
      dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === current);
        dot.setAttribute('aria-current', index === current ? 'true' : 'false');
      });
      if (previous && !config.loop) previous.disabled = track.scrollLeft <= 2;
      if (next && !config.loop) next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
    };

    const goTo = (index, behavior = 'smooth') => {
      if (config.loop) {
        index = (index + slides.length) % slides.length;
      } else {
        index = Math.max(0, Math.min(index, slides.length - 1));
      }
      track.scrollTo({
        left: slides[index].offsetLeft - track.offsetLeft,
        behavior,
      });
      current = index;
      update();
    };

    if (previous) previous.addEventListener('click', () => goTo(current - 1));
    if (next) next.addEventListener('click', () => goTo(current + 1));
    dots.forEach((dot, index) => dot.addEventListener('click', () => goTo(index)));

    track.addEventListener(
      'scroll',
      () => {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(update, 80);
      },
      { passive: true }
    );

    if (config.autoplay && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const stopAutoplay = () => {
        window.clearInterval(autoplayTimer);
        autoplayTimer = null;
      };
      const startAutoplay = () => {
        if (autoplayTimer) return;
        autoplayTimer = window.setInterval(() => goTo(current + 1), config.autoplay);
      };
      root.addEventListener('mouseenter', stopAutoplay);
      root.addEventListener('mouseleave', startAutoplay);
      root.addEventListener('focusin', stopAutoplay);
      root.addEventListener('focusout', startAutoplay);
      startAutoplay();
    }

    window.addEventListener('resize', update);
    update();
  };

  const initCarousels = (scope = document) => {
    const related = [];
    const testimonials = [];
    if (scope.matches && scope.matches('[data-sb-carousel]')) related.push(scope);
    if (scope.matches && scope.matches('[data-sb-testimonials]')) testimonials.push(scope);
    if (scope.querySelectorAll) {
      related.push(...scope.querySelectorAll('[data-sb-carousel]'));
      testimonials.push(...scope.querySelectorAll('[data-sb-testimonials]'));
    }

    related.forEach((root) =>
      setupCarousel(root, {
        track: '[data-sb-carousel-track]',
        slide: '[data-sb-carousel-slide]',
        previous: '[data-sb-carousel-prev]',
        next: '[data-sb-carousel-next]',
        dot: '[data-sb-carousel-dot]',
        loop: false,
        autoplay: 0,
      })
    );
    testimonials.forEach((root) =>
      setupCarousel(root, {
        track: '[data-sb-testimonials-track]',
        slide: '[data-sb-testimonial-slide]',
        previous: '[data-sb-testimonials-prev]',
        next: '[data-sb-testimonials-next]',
        dot: '[data-sb-testimonials-dot]',
        loop: true,
        autoplay: 5000,
      })
    );
  };

  const initHeaderParity = () => {
    const desktopPointer = window.matchMedia('(min-width: 1026px) and (hover: hover)');
    const attachHover = (details, closeDelay = 180) => {
      if (!details || initialized.has(details)) return;
      initialized.add(details);
      const summary = details.querySelector(':scope > summary');
      let closeTimer;
      let openedByHover = false;

      const setExpanded = (expanded) => {
        if (summary) summary.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      };
      const open = () => {
        window.clearTimeout(closeTimer);
        details.open = true;
        setExpanded(true);
      };
      const close = () => {
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
          if (details.dataset.sbClickLocked === 'true') return;
          details.open = false;
          openedByHover = false;
          setExpanded(false);
        }, closeDelay);
      };

      details.addEventListener('mouseenter', () => {
        if (!desktopPointer.matches) return;
        if (!details.open) openedByHover = true;
        open();
      });
      details.addEventListener('mouseleave', () => {
        if (desktopPointer.matches) close();
      });
      if (summary) {
        summary.addEventListener('click', (event) => {
          if (!desktopPointer.matches) return;

          if (details.dataset.sbClickLocked === 'true') {
            event.preventDefault();
            delete details.dataset.sbClickLocked;
            openedByHover = false;
            details.open = false;
            setExpanded(false);
            return;
          }

          if (openedByHover && details.open) {
            event.preventDefault();
            details.dataset.sbClickLocked = 'true';
            openedByHover = false;
            open();
            return;
          }

          window.setTimeout(() => {
            if (details.open) details.dataset.sbClickLocked = 'true';
          }, 0);
        });
      }
      details.addEventListener('toggle', () => setExpanded(details.open));
    };

    attachHover(document.querySelector('[data-sb-mega]'));
    attachHover(document.querySelector('[data-sb-account-menu]'));

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('[data-sb-mega][open], [data-sb-account-menu][open]').forEach((details) => {
        delete details.dataset.sbClickLocked;
        details.open = false;
        details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
      });
    });
  };

  onReady(() => {
    initShopProtect();
    initProductParity();
    initTagFilterCounts();
    initCartReminder();
    initCarousels();
    initHeaderParity();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (document.documentElement.classList.contains('sb-shop-protect')) {
            if (node.matches('img')) node.setAttribute('draggable', 'false');
            node.querySelectorAll('img').forEach((image) => image.setAttribute('draggable', 'false'));
          }
          initProductParity(node);
          initTagFilterCounts(node);
          initCarousels(node);
        });
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  });
})();